import {Component, inject} from '@angular/core';
import {Button} from 'primeng/button';
import {Password} from 'primeng/password';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormValidationService} from '../../../services/form-validation.service';
import {FormFieldComponent} from '../../inputs/form-field/form-field.component';
import {Fluid} from 'primeng/fluid';
import {TranslocoDirective, TranslocoPipe} from '@jsverse/transloco';
import {ToastService} from '@frontend/toast';
import {DynamicDialogModule, DynamicDialogRef} from 'primeng/dynamicdialog';
import {finalize} from 'rxjs';
import {RoleCheckService} from '../../../services/role-check.service';
import {
  CustomerUserControllerService,
  ProviderUserControllerService,
  UpdateCurrentCustomerPasswordRequestParams,
  UpdateCurrentProviderPasswordRequestParams,
} from '@frontend/shared-api';

@Component({
  selector: 'app-change-password-dialog',
  imports: [
    Button,
    Password,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    Fluid,
    DynamicDialogModule,
    TranslocoDirective,
    TranslocoPipe,
  ],
  templateUrl: './change-password-dialog.component.html',
})
export class ChangePasswordDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly toast = inject(ToastService);
  private readonly validations = inject(FormValidationService);
  private readonly customerUserService = inject(CustomerUserControllerService);
  private readonly providerUserService = inject(ProviderUserControllerService);
  private readonly roleCheckService = inject(RoleCheckService);
  public ref = inject(DynamicDialogRef);
  protected submitting = false;

  onHideDialog = () => {
    this.ref.close();
  };

  onSave = () => {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;

    const updateRequest$ = this.isProviderUser()
      ? this.providerUserService.updateCurrentProviderPassword(
          this.createUpdateCurrentProviderPasswordRequestParams(),
        )
      : this.customerUserService.updateCurrentCustomerPassword(
          this.createUpdateCurrentCustomerPasswordRequestParams(),
        );

    updateRequest$
      .pipe(
        finalize(() => {
          this.submitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.onHideDialog();
          this.toast.showSuccess(
            'common.save.success.header',
            'changePassword.update.success.detail',
          );
        },
        error: () => {
          this.toast.showError('error.generic', 'error.generic.detail');
        },
      });
  };

  private createUpdateCurrentCustomerPasswordRequestParams(): UpdateCurrentCustomerPasswordRequestParams {
    const formValues = this.form.value;
    return {
      updatePasswordRequest: {
        currentPassword: formValues.currentPassword!,
        newPassword: formValues.password!,
      },
    };
  }

  private createUpdateCurrentProviderPasswordRequestParams(): UpdateCurrentProviderPasswordRequestParams {
    const formValues = this.form.value;
    return {
      updateProviderPasswordRequest: {
        currentPassword: formValues.currentPassword!,
        newPassword: formValues.password!,
      },
    };
  }

  isProviderUser(): boolean {
    return (
      this.roleCheckService.hasProviderAdminRole() || this.roleCheckService.hasProviderUserRole()
    );
  }

  readonly form = this.fb.group(
    {
      currentPassword: ['', [Validators.required]],
      password: [
        '',
        [Validators.required, Validators.minLength(8), this.validations.passwordValidator()],
      ],
      passwordRepeat: ['', Validators.required],
    },
    {
      validators: this.validations.passwordMatchValidator(),
    },
  );
}
